import { AxiosError } from 'axios';
import { QueryConfig, useQuery } from 'react-query';
import BPromise from 'bluebird';

import {
  loadFolderPermissions,
  loadFoldersPermissions,
} from '~src/services/api';
import { DbFolder, DbFolderPermission } from '~src/types/db';
import { useProjectId } from '../useProjectId';
import { useFolders } from './useFolders';
import { useIsSuperuser } from '../useIsSuperuser';

interface UseFolderPermissionsParams {
  folderIds: string[];
}
export const useFolderPermissions = (
  { folderIds }: UseFolderPermissionsParams,
  queryConfig: QueryConfig<FolderPermissionData, AxiosError> & {
    enabled?: boolean;
  } = {}
) => {
  const projectId = useProjectId();
  const isSuperuser = useIsSuperuser();
  const foldersData = useFolders({
    as_admin: isSuperuser ? true : undefined,
  });

  const { enabled, ...options } = queryConfig;
  // if enabled = undefined then enabled = true
  const enabledOverwrite = enabled || enabled === undefined;

  const foldersQuery = useQuery<FolderPermissionData, AxiosError>(
    [
      'folderPermissions',
      { projectId: String(projectId), folderIds, allFolders: foldersData.data },
    ],
    loadAllFolderPermissions,
    {
      enabled: enabledOverwrite && projectId,
      refetchOnWindowFocus: false,
      ...options,
    }
  );

  return foldersQuery;
};

type FolderPermissionData = Array<{
  folderId: string;
  permissions: DbFolderPermission[];
}>;

interface LoadAllFolderPermissionsParams {
  projectId: string;
  folderIds: string[];
  allFolders: DbFolder[];
}
export const loadAllFolderPermissions = async (
  key,
  { projectId, folderIds, allFolders }: LoadAllFolderPermissionsParams
): Promise<FolderPermissionData> => {
  if (folderIds.length === 0) {
    return [];
  }
  const foldersPermissions = await loadFoldersPermissions(undefined, {
    projectId,
    folderIds,
  });

  const permissionsList = allFolders.map((item) => ({
    folderId: String(item.id),
    permissions: foldersPermissions.filter(
      (element) => element.folder_id === item.id
    ),
  }));

  return permissionsList;
};

interface UseProjectFolderPermissionsParams {
  folderIds: string[];
  projectId: string;
}

export const useProjectFolderPermissions = (
  { folderIds, projectId }: UseProjectFolderPermissionsParams,
  queryConfig: QueryConfig<FolderPermissionData, AxiosError> & {
    enabled?: boolean;
  } = {}
) => {
  const { enabled, ...options } = queryConfig;
  const enabledOverwrite = enabled || enabled === undefined;

  const foldersQuery = useQuery<FolderPermissionData, AxiosError>(
    ['projectFolderPermissions', { projectId, folderIds }],
    loadAllFolderPermissions,
    {
      enabled: enabledOverwrite && projectId,
      refetchOnWindowFocus: true,
      ...options,
    }
  );

  return foldersQuery;
};
